import type { TFunction } from 'i18next';
import { client } from 'graphql/client';
import { CONFIRM_WEBSITE_MEDIA_UPLOAD, CREATE_WEBSITE_MEDIA_UPLOAD_URL } from '../graphql/mutations';
import { WEBSITE_MEDIA_LIST } from '../graphql/queries';
import type { WebsiteMediaItem, WebsiteMediaList, WebsiteMediaType, WebsiteMediaUploadTicket } from '../graphql/types';
import { resolveWebsiteContentError } from '../utils/errorMessage';
import { formatFileSize } from '../utils/format';
import { checkMediaFile, MAX_FILE_NAME_LENGTH, MediaCheckProblem } from './mediaFormat';
import { generateImageVariants, ImageVariantFile, readImageSize } from './imageVariants';
import { readVideoInfo } from './videoInfo';
import { averageBitrate, displayDurationSeconds, inspectMp4, isH264, MAX_VIDEO_BITRATE } from './mp4Inspect';
import { findDuplicateMedia } from './duplicate';

export type MediaUploadStage = 'CHECK' | 'TRANSFER';

/** 视频专属的预检问题：编码不是 H.264、平均码率超过 8 Mbps、读不出 MP4 结构 */
export type VideoCheckProblem = 'VIDEO_CODEC' | 'VIDEO_BITRATE' | 'VIDEO_UNREADABLE';

/** 不拦截、只提示的问题：视频索引在文件末尾（未勾选网页优化） */
export type MediaUploadWarning = 'VIDEO_MOOV_AT_END';

/** 上传前预检不通过或传输到存储失败；接口错误（含全局过滤器拦截）原样抛出 */
export class MediaUploadError extends Error {
    constructor(
        readonly stage: MediaUploadStage,
        readonly problem?: MediaCheckProblem | VideoCheckProblem,
        readonly maxBytes?: number,
        readonly detail: Record<string, string | number> = {},
    ) {
        super(`website media upload failed at ${stage}${problem ? `: ${problem}` : ''}`);
    }
}

export interface UploadMediaOptions {
    /** 限定媒体类型，如封面只收图片 */
    expectedType?: WebsiteMediaType;
    /** 封面另生成 1280 宽 JPEG 分享图 */
    withOgImage?: boolean;
    /** 0~100 */
    onProgress?: (percent: number) => void;
    /** 不拦截的提示，如视频未做网页优化 */
    onWarning?: (warning: MediaUploadWarning) => void;
    /**
     * 媒体库已有同名、同大小的文件时调用；返回 true 则不上传、直接返回已有文件。不传则不查重（如正文粘贴的截图常为同名 image.png）
     */
    confirmDuplicate?: (existing: WebsiteMediaItem) => Promise<boolean>;
    signal?: AbortSignal;
}

/** 查重时按文件名取的条数：文件名为包含匹配，同名文件极少超过这个数 */
const DUPLICATE_LOOKUP_SIZE = 20;

/** 按文件名在媒体库查同名、同大小的文件；查询失败（如文件名被全局过滤器拦截）当作没有，不影响上传 */
const findExistingMedia = async (file: File, mediaType: WebsiteMediaType): Promise<WebsiteMediaItem | null> => {
    try {
        const { data } = await client.query<{ websiteMediaList: WebsiteMediaList }>({
            query: WEBSITE_MEDIA_LIST,
            variables: { mediaType, keyword: file.name, page: 1, pageSize: DUPLICATE_LOOKUP_SIZE },
            fetchPolicy: 'network-only',
        });
        return findDuplicateMedia(data?.websiteMediaList.items ?? [], file, mediaType);
    } catch {
        return null;
    }
};


/**
 * 视频上传前检查（技术文档 4.2.4）：只收 H.264（iPhone 默认的 HEVC 在部分浏览器无法播放，服务端文件头校验区分不出）；
 * 平均码率不超过 8 Mbps（官网整文件边下边播，码率高时手机网络频繁卡顿）；索引在末尾只提示。返回时长（秒）。
 */
const checkVideo = async (file: File, options: UploadMediaOptions): Promise<{ width?: number; height?: number; durationSeconds?: number }> => {
    const inspection = await inspectMp4(file).catch(() => null);
    if (!inspection?.isMp4) throw new MediaUploadError('CHECK', 'VIDEO_UNREADABLE');
    if (!isH264(inspection.videoCodec)) {
        throw new MediaUploadError('CHECK', 'VIDEO_CODEC', undefined, { codec: inspection.videoCodec ?? '-' });
    }
    const info = await readVideoInfo(file).catch(() => null);
    const exactSeconds = info?.durationSeconds || inspection.durationSeconds || undefined;
    // 码率按未取整的时长算；记录的时长取整且不足 1 秒按 1 秒
    const bitrate = averageBitrate(file.size, exactSeconds ?? null);
    if (bitrate !== null && bitrate > MAX_VIDEO_BITRATE) {
        throw new MediaUploadError('CHECK', 'VIDEO_BITRATE', undefined, { bitrate: (bitrate / 1_000_000).toFixed(1) });
    }
    if (inspection.moovAtEnd) options.onWarning?.('VIDEO_MOOV_AT_END');
    return {
        width: info?.width || undefined,
        height: info?.height || undefined,
        durationSeconds: displayDurationSeconds(exactSeconds),
    };
};

interface UploadTarget {
    url: string;
    body: Blob;
}

const UPLOAD_CONTENT_TYPE = 'multipart/form-data';

/**
 * PUT 直传到存储：请求头 Content-Type 固定为 multipart/form-data（与 storageUtils 一致，跨域规则不放行其他类型头），
 * 真实类型由确认上传时服务端校验文件头后写入。
 */
const putObject = (target: UploadTarget, onLoaded: (loaded: number) => void, signal?: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', target.url);
        xhr.setRequestHeader('Content-Type', UPLOAD_CONTENT_TYPE);
        xhr.upload.onprogress = (event) => onLoaded(event.loaded);
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                onLoaded(target.body.size);
                resolve();
            } else {
                reject(new MediaUploadError('TRANSFER'));
            }
        };
        xhr.onerror = () => reject(new MediaUploadError('TRANSFER'));
        xhr.onabort = () => reject(new MediaUploadError('TRANSFER'));
        if (signal) {
            if (signal.aborted) {
                reject(new MediaUploadError('TRANSFER'));
                return;
            }
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }
        xhr.send(target.body);
    });

const findVariantBlob = (variants: ImageVariantFile[], width: number | null, format: string | null): Blob | null =>
    variants.find((variant) => variant.width === width && variant.format === format)?.blob ?? null;

/**
 * 上传一个媒体文件（T-A10）：预检 → 读取宽高 / 时长 → 生成图片版本 → 申请上传 → 原图与各版本直传 → 确认上传。
 * 每次调用都申请新的上传链接，失败后重试直接再调一次；未确认的记录由每日任务清理。
 */
export async function uploadWebsiteMedia(file: File, options: UploadMediaOptions = {}): Promise<WebsiteMediaItem> {
    const { problem, rule } = checkMediaFile(file, options.expectedType);
    if (problem || !rule) throw new MediaUploadError('CHECK', problem ?? 'UNSUPPORTED_TYPE', rule?.maxBytes);
    if (options.confirmDuplicate) {
        const existing = await findExistingMedia(file, rule.mediaType);
        if (existing && await options.confirmDuplicate(existing)) return existing;
    }
    options.onProgress?.(0);

    let width: number | undefined;
    let height: number | undefined;
    let durationSeconds: number | undefined;
    let variants: ImageVariantFile[] = [];
    if (rule.mediaType === 'IMAGE') {
        const size = await readImageSize(file).catch(() => null);
        width = size?.width;
        height = size?.height;
        variants = await generateImageVariants(file, Boolean(options.withOgImage));
    } else if (rule.mediaType === 'VIDEO') {
        ({ width, height, durationSeconds } = await checkVideo(file, options));
    }

    const { data } = await client.mutate<{ createWebsiteMediaUploadUrl: WebsiteMediaUploadTicket }>({
        mutation: CREATE_WEBSITE_MEDIA_UPLOAD_URL,
        variables: {
            input: {
                mediaType: rule.mediaType,
                fileName: file.name,
                sizeBytes: file.size,
                variants: variants.length > 0
                    ? variants.map((variant) => ({ width: variant.width, format: variant.format, sizeBytes: variant.blob.size }))
                    : null,
            },
        },
    });
    const ticket = data?.createWebsiteMediaUploadUrl;
    if (!ticket) throw new MediaUploadError('TRANSFER');

    const targets: UploadTarget[] = [{ url: ticket.original.url, body: file }];
    ticket.variants.forEach((target) => {
        const blob = findVariantBlob(variants, target.width, target.format);
        if (blob) targets.push({ url: target.url, body: blob });
    });
    const total = targets.reduce((sum, target) => sum + target.body.size, 0);
    const loaded = targets.map(() => 0);
    const report = () => {
        const sum = loaded.reduce((acc, value) => acc + value, 0);
        // 留 1% 给确认上传
        options.onProgress?.(Math.min(99, Math.floor((sum / total) * 100)));
    };
    await Promise.all(targets.map((target, index) => putObject(target, (value) => {
        loaded[index] = value;
        report();
    }, options.signal)));

    const confirmed = await client.mutate<{ confirmWebsiteMediaUpload: WebsiteMediaItem }>({
        mutation: CONFIRM_WEBSITE_MEDIA_UPLOAD,
        variables: { mediaId: ticket.mediaId, width, height, durationSeconds },
    });
    const item = confirmed.data?.confirmWebsiteMediaUpload;
    if (!item) throw new MediaUploadError('TRANSFER');
    options.onProgress?.(100);
    return item;
}

/** 上传失败的提示文案；返回 null 表示已由全局错误处理提示 */
export function describeUploadError(e: unknown, t: TFunction): string | null {
    if (e instanceof MediaUploadError) {
        if (e.stage === 'TRANSFER') return String(t('websiteContent.upload.transferFailed'));
        return String(t(`websiteContent.upload.check.${e.problem ?? 'UNSUPPORTED_TYPE'}`, {
            maxSize: formatFileSize(e.maxBytes),
            max: MAX_FILE_NAME_LENGTH,
            ...e.detail,
        }));
    }
    return resolveWebsiteContentError(e, t);
}
